import React from 'react';
import AnimateHeight from 'react-animate-height';

import { ThemeFamilies } from 'models';
import Icon, { IconName } from 'components/core/Icon/Icon';
import Banner from 'components/widgets/Banner/Banner';
import styled, { css } from 'styled-components';
import { ButtonVariants } from 'components/core/Button/Button';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  bannerPropsSelector,
  isMobileSelector,
  isSdkSelector
} from 'redux/selectors/global';
import { hideBanner } from 'redux/actions/global';
import useRefWithCallback from 'hooks/useRefWithCallback';

interface IProps {
  className?: string;
  withAnimation?: boolean;
  duration?: number;
}

const ErrorAlert = ({ className, withAnimation, duration = 300 }: IProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const bannerProps = useSelector(bannerPropsSelector);
  const { title, text } = bannerProps ?? {};
  const errorAlertWrapperRef = useRefWithCallback<HTMLDivElement>(
    currentRef => text && currentRef?.scrollIntoView(false)
  );

  const isMobile = useSelector(isMobileSelector);
  const isSdk = useSelector(isSdkSelector);

  const isDesktop = !isMobile && !isSdk;

  const handleClick = () => dispatch(hideBanner());

  let buttonProps;

  if (isDesktop) {
    buttonProps = {
      name: 'close-button',
      children: t('close'),
      variant: ButtonVariants.TEXT,
      onClick: handleClick
    };
  }

  const content = (
    <Wrapper ref={errorAlertWrapperRef} className={className}>
      <StyledBanner
        name="error-alert"
        title={title || t('createPaymentErrors.something_went_wrong')}
        subTitle={text}
        themeFamily={ThemeFamilies.ERROR}
        customIconName={IconName.DELETE_SQ}
        buttonProps={buttonProps}
      />
      {(isMobile || isSdk) && <StyledIcon name={IconName.DELETE} onClick={handleClick} />}
    </Wrapper>
  );

  if (withAnimation) {
    return (
      <AnimateHeight height={text ? 'auto' : 0} duration={duration} className={className}>
        {content}
      </AnimateHeight>
    );
  }

  if (!text) return null;

  return content;
};

export default ErrorAlert;

export const Wrapper = styled.div`
  display: flex;
  margin-top: 1rem;

  ${({ theme }) => {
    const { isMobileNotSdk } = theme.view;

    if (isMobileNotSdk) {
      return css`
        margin-bottom: 1rem;
        padding-left: 1.5rem;
        padding-right: 1.5rem;
        position: fixed;
        z-index: 5;
      `;
    }
  }}
`;

const StyledBanner = styled(Banner)`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-end;

  button {
    margin-bottom: auto;
    > span {
      align-items: start;
    }
  }

  [data-testid='error-alert-content-wrapper'],
  [data-testid='error-alert-icon-background'] {
    align-items: unset;
  }
`;

const StyledIcon = styled(Icon)`
  font-size: ${({ theme }) => theme.fonts.size.large};
  margin-left: -3rem;
  margin-top: 1rem;
  height: fit-content;
`;
